/**
 * UrgencyTag + computeUrgency — time-sensitive status pill that
 * supersedes the StatusBadge when a rental is imminent or overdue.
 *
 * Enabled on renter variants only (per Commit 2 design decision —
 * not adding urgency to host cards as scope creep; ship later if
 * product wants it).
 *
 * Copy: "Pickup today", "Pickup tomorrow", "Return due today",
 * "Return tomorrow", "Return overdue".
 */
import React from 'react';

import css from './TransactionCard.module.css';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const startOfLocalDay = date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const daysDiff = (target, now) =>
  Math.round((startOfLocalDay(target) - startOfLocalDay(now)) / MS_PER_DAY);

const PRE_PICKUP_STATES = new Set([
  'accepted',
  'pickup-pending',
  'pickup-provider-confirmed',
  'pickup-renter-confirmed',
]);

const ACTIVE_STATES = new Set(['picked-up', 'return-pending', 'active-rental']);

/**
 * Returns { text } for an imminent/overdue booking, or null when no
 * urgency applies. Layouts call this and conditionally render either
 * <UrgencyTag text=...> or <StatusBadge state=...>.
 *
 * Pre-pickup: within 1 day of booking.start.
 * Active: within 1 day of booking.end, or past it.
 */
export const computeUrgency = (state, startDate, endDate, now = new Date()) => {
  if (PRE_PICKUP_STATES.has(state) && startDate) {
    const d = daysDiff(startDate, now);
    if (d <= 0) return { text: 'Pickup today' };
    if (d === 1) return { text: 'Pickup tomorrow' };
  }
  if (ACTIVE_STATES.has(state) && endDate) {
    const d = daysDiff(endDate, now);
    if (d < 0) return { text: 'Return overdue' };
    if (d === 0) return { text: 'Return due today' };
    if (d === 1) return { text: 'Return tomorrow' };
  }
  return null;
};

const UrgencyTag = ({ text, className }) => (
  <span className={`${css.statusBadge} ${css.statusUrgent} ${className || ''}`}>
    {text}
  </span>
);

export default UrgencyTag;
